/**
 * Keamanan Tab Component
 * 
 * Combines password change and account deletion into one tab.
 * Password: shown for own profile.
 * Delete: shown for superadmin viewing other users.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, AlertCircle, Key, Trash2 } from "lucide-react";
import { authService } from "@/services/authService";
import type { User } from "@/services/authService";
import { toast } from "sonner";
import { useLocation } from "wouter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { userService } from "@/services/userService";
import { Loader2 } from "lucide-react";

interface KeamananTabProps {
  user: User;
  isOwnProfile: boolean;
  canDelete: boolean;
}

function getConfirmTarget(user: User): { hint: string; value: string } {
  const idc = (user.id_client || "").trim();
  if (idc) {
    return { hint: "Ketik ID Client persis seperti di bawah untuk mengonfirmasi penghapusan.", value: idc };
  }
  const cs = (user.callsign || "").trim();
  if (cs) {
    return { hint: "ID Client kosong. Ketik callsign persis seperti di bawah untuk mengonfirmasi.", value: cs };
  }
  return { hint: "Ketik ID pengguna (internal) persis seperti di bawah.", value: (user.id || "").trim() };
}

function PasswordSection({ user }: { user: User }) {
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<{ current?: string; new?: string; confirm?: string; general?: string }>({});

  const validate = (pwd: string): string | null => {
    if (pwd.length < 8) return "Password harus minimal 8 karakter";
    if (!/[a-zA-Z]/.test(pwd)) return "Password harus mengandung minimal satu huruf";
    if (!/[0-9]/.test(pwd)) return "Password harus mengandung minimal satu angka";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErr = validate(newPassword);
    if (newErr) { setErrors({ new: newErr }); return; }
    if (newPassword !== confirmPassword) { setErrors({ confirm: "Konfirmasi password tidak sama" }); return; }

    setLoading(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success("Password berhasil diubah");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      setErrors({ general: err instanceof Error ? err.message : "Gagal mengubah password" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Key className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle className="text-lg">Ubah Password</CardTitle>
            <CardDescription>Perbarui kata sandi akun Anda</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="current">Password Saat Ini</Label>
            <div className="relative">
              <Input id="current" type={showCurrent ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required disabled={loading} className="pr-10" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.current && <p className="text-xs text-destructive">{errors.current}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-pwd">Password Baru</Label>
            <div className="relative">
              <Input id="new-pwd" type={showNew ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required disabled={loading} className="pr-10" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Minimal 8 karakter, huruf dan angka</p>
            {errors.new && <p className="text-xs text-destructive">{errors.new}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-pwd">Konfirmasi Password Baru</Label>
            <div className="relative">
              <Input id="confirm-pwd" type={showConfirm ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={loading} className="pr-10" />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirm && <p className="text-xs text-destructive">{errors.confirm}</p>}
          </div>

          {errors.general && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/20">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errors.general}</span>
            </div>
          )}

          <Button type="submit" disabled={loading}>
            {loading ? "Menyimpan..." : "Simpan Password"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function DeleteSection({ user }: { user: User }) {
  const [, setLocation] = useLocation();
  const [confirmText, setConfirmText] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const target = getConfirmTarget(user);
  const isMatch = confirmText.trim() === target.value;

  const handleDelete = async () => {
    if (!isMatch) return;
    setLoading(true);
    try {
      await userService.deleteUser(user._id);
      toast.success(`Pengguna ${user.callsign || user.name || user._id} berhasil dihapus`);
      setOpen(false);
      setLocation("/keanggotaan/daftar");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal menghapus pengguna");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-destructive" />
          <CardTitle className="text-destructive">Hapus Pengguna</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Menghapus pengguna akan menghapus semua data terkait termasuk riwayat pembayaran dan dokumen. Tindakan ini tidak dapat dibatalkan.
        </p>
        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm">
              <Trash2 className="h-4 w-4 mr-2" />
              Hapus Pengguna Ini
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Hapus pengguna secara permanen?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span>{target.hint}</span>
                <span className="block font-mono font-semibold text-foreground">{target.value}</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              placeholder={`Ketik ${target.value} untuk konfirmasi`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={loading}>Batal</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={!isMatch || loading}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Hapus Permanen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

export function KeamananTab({ user, isOwnProfile, canDelete }: KeamananTabProps) {
  return (
    <div className="space-y-4">
      {isOwnProfile && <PasswordSection user={user} />}
      {canDelete && <DeleteSection user={user} />}
    </div>
  );
}
